import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { setStoredLang, SupportedLang } from "../../i18n";
import AnimatedTabs from "../Common/AnimatedTabs";
import { useState } from "react";
import { TabListType } from "../../api/types/commonTypes";
import { LayoutGroup, motion } from "motion/react";

const languageTabList: TabListType[] = [
    { id: "en", label: "En" },
    { id: "hu", label: "Hu" }
];

const MotionDiv = motion.div;

function LanguageSelector() {
    const theme = useTheme();
    const { t, i18n } = useTranslation("translation", { keyPrefix: "menu" });

    // Use resolvedLanguage to avoid "en-US" vs "en" mismatch
    const current = (i18n.resolvedLanguage || i18n.language) as SupportedLang;

    // Find the index of the current lang in your tab list
    const initialIndex = languageTabList.findIndex(item => item.id === current);
    const [activeTabIndex, setActiveTabIndex] = useState(initialIndex >= 0 ? initialIndex : 0);
    const [activeTab, setActiveTab] = useState(languageTabList[activeTabIndex].id);

    const handleTabSwitch = (newId: string) => {
        setActiveTab(newId);
        const newIndex = languageTabList.findIndex(t => t.id === newId);
        setActiveTabIndex(newIndex >= 0 ? newIndex : 0);

        // update i18n + localStorage
        const lang = newId as SupportedLang;
        setStoredLang(lang);
        void i18n.changeLanguage(lang);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                paddingTop: 1
            }}
        >
            <Typography>{t("languages.language")}</Typography>
            <LayoutGroup id="lang">
                <Box
                    component={"div"}
                    sx={{
                        position: "relative",
                        display: "flex",
                        gap: "2px",
                        padding: "0.25rem",
                        background: theme.palette.animatedTabBackground,
                        borderRadius: "6px",
                        width: "fit-content"
                    }}
                >
                    {languageTabList.map(tab => {
                        const isActive = activeTab === tab.id;

                        return (
                            <div key={tab.id} style={{ position: "relative", maxHeight: "24px" }}>
                                {isActive && (
                                    <MotionDiv
                                        layoutId="langTabBg"
                                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            borderRadius: "6px",
                                            backgroundColor: theme.palette.animatedTabActiveBackground,
                                            zIndex: 0
                                        }}
                                    />
                                )}
                                <Button
                                    onClick={() => handleTabSwitch(tab.id)}
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        textTransform: "none",
                                        padding: "2px 4px",
                                        fontSize: "0.875rem",
                                        fontWeight: 500,
                                        borderRadius: "6px",
                                        minWidth: "32px",
                                        color: isActive ? theme.palette.animatedTabActiveText : theme.palette.animatedTabText,
                                        transition: "color 200ms ease-in-out",
                                        "&:hover": {
                                            backgroundColor: theme.palette.animatedTabHoverBackground,
                                            color: isActive ? theme.palette.animatedTabActiveText : theme.palette.animatedTabText
                                        }
                                    }}
                                >
                                    {tab.label}
                                </Button>
                            </div>
                        );
                    })}
                </Box>
            </LayoutGroup>
        </Box>
    );
}

export default LanguageSelector;
